import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import Dialog from 'src/components/common/Dialog';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import useFetch from 'use-http';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import 'src/styles/pages/_accountsPayable.scss';
// icons
import { BiLeftArrowAlt, BiRightArrowAlt, BiArrowToLeft, BiArrowToRight } from 'react-icons/bi';
import { FaFlagCheckered } from 'react-icons/fa';
import { MdFileUpload } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import CreditCardType from 'src/models/AP/CreditCardType';
import ChargeIndividual from 'src/models/AP/ChargeIndividual';
import CreditCardStatement from 'src/models/AP/CreditCardStatement';
import CreditCardStatementTransactionLine from 'src/models/AP/CreditCardStatementTransactionLine';
import CreditCardStatementSearch, { ICreditCardStatementSearchCriteria } from '../components/CreditCardStatementSearch';
import PersonnelToast from 'src/components/personnel/components/PersonnelToast';
import InfoDialog from './InfoDialog';

interface ICreditCardStatementDialogProps {
    dialogName: string;
    handleSelectedPerson?: (person: ChargeIndividual) => void;
    hideDialog: () => void;
    creditCardTypes: CreditCardType[];
    onStatementConverted?: (creditCardId: number) => void;
    // The Load Raw Data File dialog can't be nested inside this one (its own Dialog wrapper
    // unmounts everything inside it once isOpen goes false, which would tear down the upload
    // dialog's state along with it) - the page renders it as a sibling and coordinates via these.
    onRequestUpload: () => void;
    refreshToken?: {} | null;
}

// The web app has no native "enter query mode" like Oracle Forms, so search criteria live entirely
// in CreditCardStatementSearch. The navigation buttons and every field that shows the currently
// browsed statement are plain labels kept in sync only by those navigation buttons - no field does
// both jobs.
const CreditCardStatementDialog = (props: ICreditCardStatementDialogProps) => {
    const dispatch = useDispatch();
    const { get, post, response } = useFetch();

    const [statements, setStatements] = useState<Array<CreditCardStatement>>([]);
    const [selectedStatementIndex, setSelectedStatementIndex] = useState<number>(0);
    const [statementTransactionLines, setStatementTransactionLines] = useState<Array<CreditCardStatementTransactionLine>>([]);
    const [recordNumberInput, setRecordNumberInput] = useState<string>('');
    const [recordNumberError, setRecordNumberError] = useState<string>('');
    const [toast, setToast] = useState<{ message: string; color?: string; persistent?: boolean } | null>(null);
    const [infoMessage, setInfoMessage] = useState<string>('');
    const [infoConfirmLabel, setInfoConfirmLabel] = useState<string>(undefined);
    const [showInfoCancelButton, setShowInfoCancelButton] = useState<boolean>(false);
    const [infoOnConfirm, setInfoOnConfirm] = useState<() => void>(undefined);
    const [infoOnCancel, setInfoOnCancel] = useState<() => void>(undefined);
    // A fresh object per request (rather than the DialogList value itself) so requesting the
    // SAME dialog twice in a row still trips the effect below - useState bails out on an
    // Object.is-equal value, and a repeated enum value would go stale. Safe to keep local here
    // (unlike the Load Raw Data File dialog) since InfoDialog renders as a sibling of this own
    // Dialog within this SAME always-mounted component, not nested inside another dialog.
    const [pendingDialogToShow, setPendingDialogToShow] = useState<{ dialog: DialogList } | null>(null);

    const selectedStatement = statements?.[selectedStatementIndex];

    useEffect(() => {
        runStatementSearch({});
    }, []);

    // hideDialog() immediately followed by showDialog() in the same tick doesn't reliably swap
    // dialogs - defer the "show" to its own effect/render cycle instead of chaining it directly.
    useEffect(() => {
        if (pendingDialogToShow) {
            dispatch(actions.layout.showDialog(pendingDialogToShow.dialog));
            setPendingDialogToShow(null);
        }
    }, [pendingDialogToShow]);

    // Mirrors Oracle Forms' post-upload navigation (Go_Block('Credit_Card_Statement'); ...;
    // Query_Block(...)): the page bumps refreshToken once the sibling Load Raw Data File dialog
    // reports a successful upload, and this reloads the statement list (the new one - highest
    // ST_ID - sorts first under ORDER BY ST_ID DESC) and shows a success toast.
    useEffect(() => {
        if (props.refreshToken) {
            runStatementSearch({});
            showToast('Credit card statement file uploaded successfully.');
        }
    }, [props.refreshToken]);

    useEffect(() => {
        if (selectedStatement) {
            getStatementTransactionLines(selectedStatement.statementId);
        } else {
            setStatementTransactionLines([]);
        }
    }, [selectedStatementIndex, statements]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, color, persistent });
    };

    const hideInfoDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    // Replaces window.alert - a plain OK button, no confirm/cancel.
    const showAlert = (message: string) => {
        setInfoMessage(message);
        setInfoConfirmLabel(undefined);
        setShowInfoCancelButton(false);
        setInfoOnConfirm(undefined);
        setInfoOnCancel(undefined);
        setPendingDialogToShow({ dialog: DialogList.INFO_STATEMENT });
    };

    // Replaces window.confirm - OK (running onConfirm) or Cancel.
    const showConfirm = (message: string, onConfirm: () => void) => {
        setInfoMessage(message);
        setInfoConfirmLabel('OK');
        setShowInfoCancelButton(true);
        setInfoOnConfirm(() => onConfirm);
        setInfoOnCancel(undefined);
        setPendingDialogToShow({ dialog: DialogList.INFO_STATEMENT });
    };

    const getStatementTransactionLines = async (statementId: number) => {
        const result = await get(`/v1.0/AccountsPayable/creditcardstatements/${statementId}/transactions`);
        if (response.ok && result) {
            setStatementTransactionLines(result.items ?? []);
        }
    };

    const runStatementSearch = async (criteria: ICreditCardStatementSearchCriteria) => {
        const searchOptions = new ODataOptions();

        if (criteria.creditCardType !== undefined) {
            searchOptions.addFilter(new ODataFilter('CC_NO', ODataFilterOperators.Equals, criteria.creditCardType));
        }
        if (criteria.statementDate !== undefined) {
            searchOptions.addFilter(new ODataFilter('ST_DATE', ODataFilterOperators.Equals, criteria.statementDate.toISOString().split('T')[0]));
        }
        if (criteria.periodBeginDate !== undefined) {
            searchOptions.addFilter(new ODataFilter('BEG_DATE', ODataFilterOperators.Equals, criteria.periodBeginDate.toISOString().split('T')[0]));
        }
        if (criteria.periodEndDate !== undefined) {
            searchOptions.addFilter(new ODataFilter('END_DATE', ODataFilterOperators.Equals, criteria.periodEndDate.toISOString().split('T')[0]));
        }

        searchOptions.addOrderBy('ST_ID', ODataOrderByDirection.Descending);

        const result = await get(`/v1.0/AccountsPayable/creditcardstatements${searchOptions.toString()}`);

        if (response.ok && result) {
            setStatements(result.items ?? []);
            setSelectedStatementIndex(0);
        }
    };

    const moveToFirstStatement = () => setSelectedStatementIndex(0);

    const moveToPreviousStatement = () => {
        setSelectedStatementIndex((index) => (index - 1 >= 0 ? index - 1 : index));
    };

    const moveToNextStatement = () => {
        setSelectedStatementIndex((index) => (index + 1 < statements.length ? index + 1 : index));
    };

    const moveToLastStatement = () => setSelectedStatementIndex(statements.length > 0 ? statements.length - 1 : 0);

    // "n of x" jump-to-record, mirroring the same pattern in CreditCardsRecPage.tsx/
    // DepositReconciliationPage.tsx: refs keep the debounced validator reading current
    // values without needing to be re-created (and losing its pending debounce) on every
    // navigation.
    const statementsRef = useRef(statements);
    const selectedStatementIndexRef = useRef(selectedStatementIndex);
    useEffect(() => {
        statementsRef.current = statements;
    }, [statements]);
    useEffect(() => {
        selectedStatementIndexRef.current = selectedStatementIndex;
    }, [selectedStatementIndex]);

    useEffect(() => {
        setRecordNumberInput(statements && statements.length > 0 ? `${selectedStatementIndex + 1}` : '');
        setRecordNumberError('');
    }, [selectedStatementIndex, statements]);

    const validateAndJumpToRecordNumber = useRef(_.debounce((rawValue: string) => {
        const trimmed = rawValue.trim();

        if (!trimmed) {
            setRecordNumberError('');
            return;
        }

        if (!/^\d+$/.test(trimmed)) {
            setRecordNumberError('Record # must be a whole number.');
            return;
        }

        const currentStatements = statementsRef.current;

        if (!currentStatements || currentStatements.length === 0) {
            setRecordNumberError('No statements are loaded.');
            return;
        }

        const enteredNumber = parseInt(trimmed, 10);

        if (enteredNumber < 1 || enteredNumber > currentStatements.length) {
            setRecordNumberError(`Record # must be between 1 and ${currentStatements.length}.`);
            return;
        }

        setRecordNumberError('');

        const targetIndex = enteredNumber - 1;
        if (targetIndex !== selectedStatementIndexRef.current) {
            setSelectedStatementIndex(targetIndex);
        }
    }, 800)).current;

    const handleRecordNumberInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setRecordNumberInput(value);
        validateAndJumpToRecordNumber(value);
    };

    // Mirrors Oracle Forms' WHEN-BUTTON-PRESSED [oid 545]: guard on no statement selected / already
    // processed (DUMP_CC_ID set), confirm, then convert - the actual master/detail row creation and
    // DUMP_DATE/DUMP_CC_ID stamping happens in the ConvertCreditCardStatement stored procedure.
    const convertStatement = () => {
        if (!selectedStatement) {
            showAlert('No Credit Card statement available to process.');
            return;
        }

        if (selectedStatement.processedCreditCardId != null) {
            showAlert('This Credit Card Statement has already been processed.');
            return;
        }

        showConfirm(
            'This will create a new Credit Card Transaction. Are you sure you want to process this statement?',
            () => performConvertStatement()
        );
    };

    const performConvertStatement = async () => {
        const result = await post(`/v1.0/AccountsPayable/creditcardstatements/${selectedStatement.statementId}/convert`);

        if (response.ok && result?.creditCardId) {
            props.onStatementConverted?.(result.creditCardId);
            runStatementSearch({});
            showToast('Credit card statement processed successfully.');
        } else {
            showAlert('Unable to process this Credit Card Statement.');
        }
    };

    const formatDateForDisplay = (date: Date | string | null | undefined) => {
        const parsed = moment(date);
        return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '';
    };

    const creditCardTypeName = props.creditCardTypes?.find((c) => `${c.id}` === `${selectedStatement?.creditCardType}`)?.name
        ?? selectedStatement?.creditCardType
        ?? '';

    return (
        <>
        <Dialog title="Credit Card Statement Maintenance" dialogName={props.dialogName} isExitButtonInvisible={false} bodyStyle={{ width: '65%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <CreditCardStatementSearch creditCardTypes={props.creditCardTypes} runQuery={runStatementSearch} />
            <Page className="dlc-maxer-width">
                <Page.Section width={14}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle="Statement Transactions">
                            <div style={{ padding: '.5rem' }}>
                                <table style={{ width: '100%', margin: '0 auto' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Statement No.</th>
                                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Credit Card</th>
                                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Statement Date</th>
                                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Period Dates</th>
                                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Date Uploaded</th>
                                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Proc. Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 'bold' }}>{selectedStatement?.statementId ?? ''}</td>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 'bold' }}>{creditCardTypeName}</td>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 'bold' }}>{formatDateForDisplay(selectedStatement?.statementDate)}</td>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                                                {formatDateForDisplay(selectedStatement?.periodBeginDate)} to {formatDateForDisplay(selectedStatement?.periodEndDate)}
                                            </td>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 'bold' }}>{formatDateForDisplay(selectedStatement?.dateUploaded)}</td>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 'bold' }}>{formatDateForDisplay(selectedStatement?.processedDate)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div className="form-row" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                                    <Button
                                        style={{ height: '3rem', background: 'transparent' }}
                                        text={''}
                                        title="Move to First Record"
                                        reactIcon={BiArrowToLeft}
                                        iconHoverColor="blue"
                                        iconStyle={{ width: '2rem', height: '2rem', verticalAlign: 'center' }}
                                        onClick={() => moveToFirstStatement()}
                                    />
                                    <Button
                                        style={{ height: '3rem', marginLeft: '1rem', background: 'transparent' }}
                                        text={''}
                                        title="Move to Previous Record"
                                        reactIcon={BiLeftArrowAlt}
                                        iconHoverColor="blue"
                                        iconStyle={{ width: '2rem', height: '2rem', verticalAlign: 'center' }}
                                        onClick={() => moveToPreviousStatement()}
                                    />
                                    <Button
                                        style={{ height: '3rem', marginLeft: '1rem', background: 'transparent' }}
                                        text={''}
                                        title="Move to Next Record"
                                        reactIcon={BiRightArrowAlt}
                                        iconHoverColor="blue"
                                        iconStyle={{ width: '2rem', height: '2rem', verticalAlign: 'center' }}
                                        onClick={() => moveToNextStatement()}
                                    />
                                    <Button
                                        style={{ height: '3rem', marginLeft: '1rem', background: 'transparent' }}
                                        text={''}
                                        title="Move to Last Record"
                                        reactIcon={BiArrowToRight}
                                        iconHoverColor="blue"
                                        iconStyle={{ width: '2rem', height: '2rem', verticalAlign: 'center' }}
                                        onClick={() => moveToLastStatement()}
                                    />
                                    <input
                                        id="statementRecordNumberInput"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={3}
                                        value={recordNumberInput}
                                        onChange={(e) => handleRecordNumberInputChange(e)}
                                        title="Enter a record # to jump to it"
                                        style={{
                                            fontWeight: 'bold',
                                            marginLeft: '1rem',
                                            width: '3rem',
                                            textAlign: 'center',
                                            border: recordNumberError ? '2px solid red' : '1px solid #ccc',
                                            borderRadius: '4px',
                                            outline: 'none'
                                        }}
                                    />
                                    <label style={{marginLeft: '.5rem', marginRight: '.5rem'}}>of</label>
                                    <label style={{fontWeight: 'bold'}}>{statements?.length ?? 0}</label>
                                    {recordNumberError &&
                                        <label style={{marginLeft: '1rem', color: 'red', fontWeight: 'bold', whiteSpace: 'nowrap'}}>
                                            {recordNumberError}
                                        </label>}
                                    <Button
                                        style={{ height: '3rem', marginLeft: '2rem', background: 'transparent' }}
                                        text={''}
                                        title="Upload"
                                        reactIcon={MdFileUpload}
                                        iconHoverColor="blue"
                                        iconStyle={{ width: '2.2rem', height: '2.2rem', verticalAlign: 'center' }}
                                        onClick={() => props.onRequestUpload()}
                                    />
                                    <Button
                                        style={{ height: '3rem', marginLeft: '1rem', background: 'transparent' }}
                                        text={''}
                                        title="Convert Statement"
                                        reactIcon={FaFlagCheckered}
                                        iconHoverColor="blue"
                                        iconStyle={{ width: '2.2rem', height: '2.2rem', verticalAlign: 'center' }}
                                        onClick={() => convertStatement()}
                                    />
                                </div>
                                <div style={{ marginTop: '1rem', maxHeight: '20rem', overflowY: 'auto' }}>
                                    <table style={{ width: '100%' }}>
                                        <thead style={{ textAlign: 'center' }}>
                                            <tr>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '15%' }}>Transaction Type</th>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '8%' }}>Tran. Date</th>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '25%' }}>Transaction Description</th>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '8%' }}>Amount</th>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '10%' }}>Tran. No.</th>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '6%' }}>Charged By</th>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '10%' }}>Employee</th>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '6%' }}>Vendor No.</th>
                                                <th className="find-person-header" style={{ fontSize: '9px', width: '12%' }}>Vendor Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {statementTransactionLines?.map((line: CreditCardStatementTransactionLine) => {
                                                return (
                                                    <tr key={line.transactionId} style={{ textAlign: 'left' }}>
                                                        <td style={{ width: '15%' }}>{line.transactionType}</td>
                                                        <td style={{ width: '8%' }}>{formatDateForDisplay(line.transactionDate)}</td>
                                                        <td style={{ width: '25%' }}>{line.transactionDescription}</td>
                                                        <td style={{ width: '8%' }}>{line.transactionAmount}</td>
                                                        <td style={{ width: '10%' }}>{line.transactionNumber}</td>
                                                        <td style={{ width: '6%' }}>{line.chargedBy}</td>
                                                        <td style={{ width: '10%' }}>{line.employeeName}</td>
                                                        <td style={{ width: '6%' }}>{line.vendorNumber}</td>
                                                        <td style={{ width: '12%' }}>{line.vendorName}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%' }}
                    text={'Close'}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
        <InfoDialog
            dialogName={DialogList.INFO_STATEMENT}
            hideDialog={hideInfoDialog}
            message={infoMessage}
            confirmLabel={infoConfirmLabel}
            showCancelButton={showInfoCancelButton}
            handleConfirm={infoOnConfirm}
            handleCancellation={infoOnCancel}
        />
        </>
    );
};

export default CreditCardStatementDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/CreditCardStatementDialog.tsx